import React, { useMemo, useState } from 'react';
import { Table, Button, Pagination, Space, Empty, Statistic, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { formatCentsAmount, formatYyyymmdd } from '../../../../utils/invoiceAmount';
import SubmitInvoiceApplicationModal from './components/SubmitInvoiceApplicationModal';
import {
    GET_REALTIME_DAILY_AGGREGATES,
    GetRealtimeDailyAggregatesResponse,
} from './graphql/queries/getRealtimeDailyAggregates';
import {
    GET_REALTIME_INVOICE_OVERVIEW,
    GetRealtimeInvoiceOverviewResponse,
} from './graphql/queries/getRealtimeInvoiceOverview';
import { RealtimeDailyAggregateType } from './graphql/types/RealtimeInvoiceType';
import { todayDateRange } from './config/filterOptions';

interface RealtimeInvoicePoolOverviewPageProps {
    settlementClientId?: string;
}

/** 一次开具 Invoice 最多可勾选的日期数 */
const MAX_SELECTABLE_DATES = 15;

/** 将 YYYY-MM-DD 字符串转 YYYYMMDD number */
const toYyyymmdd = (s: string | null): number | undefined => {
    if (!s) return undefined;
    const n = Number(s.replace(/[-/]/g, ''));
    return Number.isNaN(n) ? undefined : n;
};

const RealtimeInvoicePoolOverviewPage: React.FC<RealtimeInvoicePoolOverviewPageProps> = ({ settlementClientId }) => {
    const { t } = useTranslation();
    const canOperate = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]);

    const [tempRange, setTempRange] = useState<[string | null, string | null] | null>(todayDateRange());
    const [range, setRange] = useState<[string | null, string | null] | null>(todayDateRange());
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
    const [submitOpen, setSubmitOpen] = useState(false);

    const overviewQuery = useQuery<GetRealtimeInvoiceOverviewResponse>(GET_REALTIME_INVOICE_OVERVIEW, {
        variables: { settlementClientId },
        skip: !settlementClientId,
        fetchPolicy: 'cache-and-network',
    });
    const overview = overviewQuery.data?.getRealtimeInvoiceOverview;

    const listQuery = useQuery<GetRealtimeDailyAggregatesResponse>(GET_REALTIME_DAILY_AGGREGATES, {
        variables: {
            settlementClientId,
            dateFrom: toYyyymmdd(range?.[0] ?? null) ?? null,
            dateTo: toYyyymmdd(range?.[1] ?? null) ?? null,
            page,
            size,
        },
        skip: !settlementClientId,
        fetchPolicy: 'cache-and-network',
    });
    const result = listQuery.data?.getRealtimeDailyAggregates;
    const rows = result?.content ?? [];
    const total = result?.pageInfo?.totalElements ?? 0;

    const selectedAggregates = useMemo(
        () => rows.filter((r) => selectedKeys.includes(r.aggregateId)),
        [rows, selectedKeys],
    );

    const handleFilter = () => {
        setRange(tempRange);
        setPage(1);
        setSelectedKeys([]);
    };

    const refreshAll = () => {
        overviewQuery.refetch();
        listQuery.refetch();
        setSelectedKeys([]);
    };

    const columns: ColumnsType<RealtimeDailyAggregateType> = [
        { title: t('settlement.realtimeInvoice.columns.date'), dataIndex: 'businessDate', render: (v: number) => formatYyyymmdd(v) },
        { title: t('settlement.realtimeInvoice.columns.delivered'), dataIndex: 'deliveredCount' },
        { title: t('settlement.realtimeInvoice.columns.returned'), dataIndex: 'returnedCount' },
        { title: t('settlement.realtimeInvoice.columns.totalOrders'), dataIndex: 'orderCount' },
        { title: t('settlement.realtimeInvoice.columns.totalAmount'), dataIndex: 'totalAmount', render: (v: number, r) => formatCentsAmount(v, r.currency ?? overview?.currency) },
        { title: t('settlement.realtimeInvoice.columns.invoiced'), dataIndex: 'invoicedAmount', render: (v: number, r) => formatCentsAmount(v, r.currency ?? overview?.currency) },
        { title: t('settlement.realtimeInvoice.columns.remaining'), dataIndex: 'remainingAmount', render: (v: number, r) => formatCentsAmount(v, r.currency ?? overview?.currency) },
    ];

    if (!settlementClientId) {
        return <Empty description={t('settlement.realtimeInvoice.selectClientHint')} />;
    }

    return (
        <div>
            <div className="mb-4 flex items-center gap-12">
                <Statistic
                    title={t('settlement.realtimeInvoice.totalRemaining')}
                    value={formatCentsAmount(overview?.totalRemainingAmount, overview?.currency)}
                />
                <Statistic
                    title={t('settlement.realtimeInvoice.totalInvoiced')}
                    value={formatCentsAmount(overview?.totalInvoicedAmount, overview?.currency)}
                />
            </div>

            <div className="mb-4 flex items-end gap-4">
                <DateRangeFilterItem
                    label={t('settlement.realtimeInvoice.columns.date')}
                    value={tempRange}
                    onChange={setTempRange}
                    spanClassName="w-[9em]"
                />
                <Button type="primary" onClick={handleFilter}>
                    {t('settlement.realtimeInvoice.actions.filter')}
                </Button>
            </div>

            <div className="mb-3 flex items-center justify-end gap-3">
                <span className="text-gray-500">
                    {t('settlement.realtimeInvoice.selectedCount', {
                        selected: selectedAggregates.length,
                        max: MAX_SELECTABLE_DATES,
                    })}
                </span>
                <Space>
                    <Button
                        type="primary"
                        disabled={!canOperate || selectedAggregates.length === 0}
                        onClick={() => setSubmitOpen(true)}
                    >
                        {t('settlement.realtimeInvoice.actions.submit')}
                    </Button>
                </Space>
            </div>

            <Table
                rowKey="aggregateId"
                columns={columns}
                dataSource={rows}
                loading={listQuery.loading}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedKeys,
                    onChange: (keys) => {
                        // 超限整体拦下（含表头全选），不截取前 N 条——静默改写用户意图更难理解
                        if (keys.length > MAX_SELECTABLE_DATES) {
                            message.warning(
                                t('settlement.realtimeInvoice.maxDatesExceeded', { max: MAX_SELECTABLE_DATES }),
                            );
                            return;
                        }
                        setSelectedKeys(keys);
                    },
                    getCheckboxProps: (record) => ({
                        disabled: !(record.remainingAmount != null && record.remainingAmount > 0),
                    }),
                }}
            />

            <div className="mt-4 flex items-center justify-between">
                <div className="text-gray-500">{total} {t('settlement.realtimeInvoice.results')}</div>
                <Pagination
                    current={page}
                    total={total}
                    pageSize={size}
                    size="small"
                    showSizeChanger
                    onChange={(p, s) => {
                        setPage(p);
                        setSize(s);
                        setSelectedKeys([]);
                    }}
                />
            </div>

            <SubmitInvoiceApplicationModal
                open={submitOpen}
                settlementClientId={settlementClientId}
                selectedAggregates={selectedAggregates}
                currency={overview?.currency}
                customerName={overview?.customerName}
                channelId={overview?.channelId}
                corporateEntityTitle={overview?.corporateEntityTitle}
                netDay={overview?.netDay}
                onCancel={() => setSubmitOpen(false)}
                onSuccess={() => {
                    setSubmitOpen(false);
                    refreshAll();
                }}
            />
        </div>
    );
};

export default RealtimeInvoicePoolOverviewPage;
